import {useMenuItem} from '@react-aria/menu';
import {useHover} from '@react-aria/interactions';
import type {Item} from '@react-stately/collections';
import type {Key, Node} from '@react-types/shared';
import type {TreeState} from '@react-stately/tree';
import React from 'react';
import cx from 'classnames';

import type {AriaMenuItem} from './AriaMenu';

import styles from './AriaMenu.module.scss';

export const InnerAriaMenuItem: React.FC<{
    item: Node<typeof Item>;
    state: TreeState<typeof Item>;
    onAction?: (key: Key) => void;
    itemMap: Map<Key, AriaMenuItem>;
}> = ({item, state, onAction, itemMap}) => {
    const ref = React.useRef(null);

    const {isHovered, hoverProps} = useHover({
        onHoverStart: () => state.selectionManager.setFocusedKey(item.key),
        onHoverEnd: () => {
            if (state.selectionManager.focusedKey === item.key) {
                state.selectionManager.setFocusedKey(null);
            }
        },
    });

    const {menuItemProps} = useMenuItem(
        {
            key: item.key,
            onAction,
        },
        state,
        ref,
    );

    // Handle focus events so we can apply highlighted
    const isFocused = state.selectionManager.focusedKey === item.key;

    const alternativeHoverClassName = itemMap.get(
        item.key,
    )?.alternativeHoverClassName;

    const alternativeHoverStyle = itemMap.get(item.key)?.alternativeHoverStyle;

    // Highlight if focused or hovered but not both to allow keyboard navigation even if mouse is over the item
    const shouldHighlight = isFocused || (isFocused && !isHovered);

    return (
        <div
            {...menuItemProps}
            {...hoverProps}
            data-testid={itemMap.get(item.key)?.testId}
            ref={ref}
            style={{
                ...itemMap.get(item.key)?.style,
                ...(shouldHighlight ? alternativeHoverStyle : {}),
            }}
            className={cx(
                itemMap.get(item.key)?.className,
                styles.innerMenuItem,
                shouldHighlight
                    ? alternativeHoverClassName ?? styles.hovered
                    : {},
                'px-2',
                'py-2',
            )}
        >
            {itemMap.get(item.key)?.icon}
            {item.rendered}
        </div>
    );
};
